import { Component, OnInit, ViewChild } from '@angular/core';
import { ToastController, LoadingController } from '@ionic/angular';
import { BaseClass } from 'src/app/shared/classes/base';
import { TranslationService } from 'src/app/shared/services/translation.service';
import { Router, ActivatedRoute } from '@angular/router';
import { ProcessConfig, QuestionGroup } from '../shared/interfaces/process-config.interface';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { ProcessStatusEnum } from '../shared/interfaces/process.interface';
import { UtilService } from '../shared/services/util.service';
import { AutoSaveComponent } from '../shared/components/autosave/autosave.component';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import { SelfEvaluationMidYearService } from './self-evaluation-mid-year.service';
import { SelfEvaluationRecord } from '../self-evaluation/_interfaces/self-evaluation-record.interface';
import { ConfigService } from '../shared/services/multi-clients/config.service';

@Component({
  selector: 'app-self-evaluation-mid-year',
  templateUrl: './self-evaluation-mid-year.page.html',
  styleUrls: ['./self-evaluation-mid-year.page.scss']
})
export class SelfEvaluationMidYearPage extends BaseClass implements OnInit {

  @ViewChild('autosave', { static: true }) autosave: AutoSaveComponent;

  public processConfig: ProcessConfig;
  public formGroup: UntypedFormGroup;
  public status: ProcessStatusEnum;
  public allowAutoSave: boolean = true;
  public autoSaving: boolean = false;
  public timerStart: boolean = false;
  public hasPendingChanges: boolean = false;
  public hasTwoPlusTwoCoaching: boolean = false;
  public timerId;
  public questionsType: any;

  private _cycleId: number;

  constructor(
    protected _toastController: ToastController,
    protected _translationService: TranslationService,
    private _configService: ConfigService,
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _evaluationService: SelfEvaluationMidYearService,
    private _loadingController: LoadingController,
    private _utilService: UtilService
  ) {
    super(_toastController, _translationService);
  }

  ngOnInit() {
    this.questionsType = this._configService.settings.features.questionTypeAvailability.question;
    this._cycleId = parseInt(
      this._activatedRoute.snapshot.paramMap.get('cycleId'), 10
    );
  }

  ionViewWillEnter() {
    this._loadEvaluation();
  }

  public goBack(): void {
    this._router.navigate([ 'dashboard' ]);
  }

  public goToNotepad(): void {
    this._setReturnPath('Notepad_ReturnPath');
    this._router.navigate([ 'notepad' ]);
  }

  public goToCoaching(): void {
    this._setReturnPath('GoalsReview_ReturnPath');
    this._router.navigate([ 'goals-review/' + this._cycleId ]);
  }

  public save(
    formGroup: UntypedFormGroup, preventNotification: boolean = false
  ): void {
    this.autoSaving = true;

    const evaluation = this._utilService.adjustRatingsForGoals(
      this.processConfig.questionsGroups,
      formGroup.getRawValue()
    ) as SelfEvaluationRecord;

    this.timerStart = false;
    clearTimeout(this.timerId);

    this._evaluationService.saveSelfEvaluation(
      evaluation
    ).subscribe(() => {
      if (!preventNotification) {
        this.hasPendingChanges = false;
        this.notifySuccess();
      }

      this.autoSaving = false;

    }, (error) => {
      this.notifyError(error);
      this.autoSaving = false;
    });
  }

  public send(formGroup: UntypedFormGroup): void {
    if (!formGroup.valid) {
      formGroup['missingFields'] = true;

      this.notify(
        this._translationService.getTranslation(
          'generic.requiredValueNotProvided'
        ), 'danger'
      );

      return;
    }

    const evaluation = this._utilService.adjustRatingsForGoals(
      this.processConfig.questionsGroups,
      formGroup.getRawValue()
    ) as SelfEvaluationRecord;

    this._evaluationService.sendSelfEvaluation(
      evaluation
    ).subscribe(() => {
      this.notifySuccess();
      this.goBack();

    }, (error) => this.notifyError(error) );
  }

  private _createFormGroup(
    groups: Array<QuestionGroup>, editingEvaluation: SelfEvaluationRecord
  ): UntypedFormGroup {
    const formGroup = new UntypedFormGroup({
      'cycleId': new UntypedFormControl( this._cycleId )
    });

    const goalsIndexes = this._utilService.getGoalsFieldsIndexes( groups );
    let currentIndex = 1;

    if (groups) {
      groups.forEach((group) => {
        group.questions.forEach((question) => {
          const controlName = 'field' + (currentIndex);
          const validators = question.required || question.goal ? [ Validators.required ] : [];

          formGroup.addControl(
            controlName,
            new UntypedFormControl({
              'value': this._utilService.getEvaluationValue(question, editingEvaluation, goalsIndexes, currentIndex ),
              'disabled': this.status !== ProcessStatusEnum.WaitingInput
            }, validators)
          );

          question.currentIndex = currentIndex;
          currentIndex++;
        });
      });
    }

    return formGroup;
  }

  private async _loadEvaluation(): Promise<void> {
    const loading = await this._loadingController.create();
    await loading.present();

    this._evaluationService.getSelfEvaluation(
      this._cycleId
    ).subscribe((response) => {
      this.processConfig = response.data.config;
      this.status = response.data.status;
      this.hasTwoPlusTwoCoaching = response.data.hasTwoPlusTwoCoaching;

      this.processConfig.questionsGroups = this._utilService.addGoalsAsQuestions(
        response.data.goals, this.processConfig.questionsGroups
      );

      const formGroup = this._createFormGroup(
        this.processConfig.questionsGroups,
        response.data.evaluation
      );

      if (this.processConfig.questionsGroups) {
        this.processConfig.questionsGroups.forEach(group => {
          group.questions = this._utilService.groupQuestions(
            group.questions
          );
        });
      }

      this.formGroup = formGroup;

      if (this.autosave) {
        this.autosave.autoSaveAllowed$.subscribe((value: boolean) => {
          this.allowAutoSave = value;
        });
      }

      this.formGroup.valueChanges
      .pipe(
          distinctUntilChanged(),
          debounceTime(15000)
      )
      .subscribe(() => {
        if (this.allowAutoSave && !this.timerStart) {
          this.timerStart = true;
          this._autoSave();
        }
      });

      loading.dismiss();

    }, (error) => {
      loading.dismiss();
      this.notifyError(error);
    });
  }

  private _autoSave() {
    this.timerId = setTimeout(() => {
      this.timerStart = false;
      this.hasPendingChanges = this.autoSaving && true;
      if (!this.autoSaving) {
          this.save(
            this.formGroup,
            true
        );
      }
     }, 60000 );
  }

  private _setReturnPath(storageKey: string): void {
    localStorage.setItem(
      storageKey,
      'self-evaluation/' + this._cycleId
    );
  }

}
